import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { FaWallet, FaSignOutAlt, FaHistory, FaDownload, FaCheckCircle, FaCopy, FaShieldAlt } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import apiService from '../utils/apiService';
import UniversalBalancePanel from '../components/UniversalBalancePanel';
import TokenCarousel from '../components/TokenCarousel';
import TokenDetailDrawer from '../components/TokenDetailDrawer';
import { ActivitySkeleton } from '../components/LoadingSkeleton';

export default function Dashboard() {
  const [portfolio, setPortfolio] = useState({ total_usd: 0, total_tokens: 0, tokens: [] });
  const [addresses, setAddresses] = useState([]);
  const [activity, setActivity] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [showBalance, setShowBalance] = useState(true);
  const [selectedToken, setSelectedToken] = useState(null);
  const [selectedAddressForDeposit, setSelectedAddressForDeposit] = useState(null);
  const [depositAmount, setDepositAmount] = useState('10');
  const [depositToken, setDepositToken] = useState('USDT');
  const [allTokens, setAllTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setInitialLoading(true);
      const [portfolioRes, addrRes, actRes, userRes, tokensRes] = await Promise.all([
        apiService.getPortfolio(),
        apiService.getAddresses(),
        apiService.getActivity(),
        apiService.getUserInfo(),
        apiService.getSupportedTokens(),
      ]);

      setPortfolio(portfolioRes.data);
      setAddresses(addrRes.data);
      setActivity(actRes.data);
      setUserInfo(userRes.data);
      setAllTokens(tokensRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const handleMockDeposit = async () => {
    if (!selectedAddressForDeposit) return;
    
    setLoading(true);
    try {
      await apiService.mockDeposit({
        to_address: selectedAddressForDeposit.address,
        chain: selectedAddressForDeposit.chain,
        amount: parseFloat(depositAmount),
        token: depositToken,
      });

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      toast.success(`Mock deposit of ${depositAmount} ${depositToken} successful!`);
      await loadData();
      setSelectedAddressForDeposit(null);
    } catch (error) {
      console.error('Mock deposit failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const exportTransactions = () => {
    const csv = [
      ['Date', 'Type', 'Chain', 'Token', 'Amount', 'TxHash', 'Status'],
      ...activity.map((a) => [
        new Date(a.created_at).toLocaleString(),
        a.type,
        a.chain,
        a.token,
        a.amount,
        a.txhash,
        a.status,
      ]),
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'transactions.csv';
    a.click();
    toast.success('Transactions exported!');
  };

  const handleLogout = async () => {
    try {
      await apiService.logout();
      localStorage.removeItem('token');
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      // Even if API call fails, clear local token
      localStorage.removeItem('token');
      navigate('/login');
    }
  };

  const isCustodial = userInfo?.custody_mode === 'custodial';

  return (
    <div className="min-h-screen">
      {/* Navbar */}
      <nav className="glass-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <FaWallet className="text-[#ccff00] text-2xl mr-3" />
            <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
              Universal Balance
            </h1>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center text-[#94a3b8] hover:text-[#ccff00] transition-colors"
            data-testid="logout-button"
          >
            <FaSignOutAlt className="mr-2" />
            <span className="text-sm uppercase tracking-wider">Logout</span>
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Universal Balance Section */}
        <div className="mb-12 fade-in-up">
          {/* Balance Display */}
          <div className="bg-[#0f172a] border border-[#1e293b] p-8 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#94a3b8] mb-2">Universal Balance</p>
                {isCustodial && (
                  <div className="inline-flex items-center bg-[#ccff00] text-black px-3 py-1 text-xs font-bold uppercase">
                    <FaShieldAlt className="mr-1" /> Custodial Vault
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowBalance(!showBalance)}
                className="text-[#94a3b8] hover:text-[#ccff00] transition-colors text-sm uppercase tracking-wider"
                data-testid="toggle-balance-visibility"
              >
                {showBalance ? 'Hide Balance' : 'Show Balance'}
              </button>
            </div>

            {/* Balance Amount */}
            {showBalance ? (
              initialLoading ? (
                <div className="animate-pulse">
                  <div className="h-20 w-64 bg-[#1e293b] mb-2" />
                </div>
              ) : portfolio.tokens.length === 0 ? (
                <div className="text-center py-4">
                  <h2 className="text-6xl md:text-7xl font-extrabold text-white mb-2" style={{ fontFamily: 'Manrope' }}>
                    $0.00
                  </h2>
                  <p className="text-[#94a3b8] text-sm">No balance yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-center py-4">
                    <h2 className="text-6xl md:text-7xl font-extrabold text-white mb-2" style={{ fontFamily: 'Manrope' }} data-testid="total-balance-usd">
                      ${portfolio.total_usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </h2>
                    <p className="text-[#ccff00] text-lg uppercase tracking-wider" style={{ fontFamily: 'Manrope' }}>
                      Across {portfolio.total_tokens} {portfolio.total_tokens === 1 ? 'Token' : 'Tokens'}
                    </p>
                  </div>
                  {/* Token breakdown */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {portfolio.tokens.map((token) => (
                      <div key={token.symbol} className="bg-[#020617] border border-[#1e293b] p-3">
                        <p className="text-xs text-[#94a3b8] uppercase mb-1">{token.symbol}</p>
                        <p className="text-lg font-bold text-white mono mb-1">{token.balance.toFixed(2)}</p>
                        {token.price_usd !== undefined && (
                          <p className="text-xs text-[#64748b] mb-1">@ ${token.price_usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        )}
                        <p className="text-xs text-[#ccff00] font-medium">${token.balance_usd.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )
            ) : (
              <div className="text-center py-4">
                <p className="text-2xl text-[#94a3b8]">••••••</p>
                <p className="text-sm text-[#94a3b8] mt-2">Balance hidden</p>
              </div>
            )}
          </div>

          {/* Price Breakdown Section - For Transparency */}
          {portfolio.tokens.length > 0 && (
            <div className="bg-[#0f172a] border border-[#1e293b] p-6 mb-6">
              <h3 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
                💰 Balance Breakdown
              </h3>
              <div className="space-y-2">
                {portfolio.tokens.map((token) => (
                  <div key={token.symbol} className="flex items-center justify-between py-2 border-b border-[#1e293b]">
                    <div className="flex items-center gap-3">
                      <span className="text-white font-medium">{token.balance.toFixed(2)} {token.symbol}</span>
                      <span className="text-[#64748b]">×</span>
                      <span className="text-[#94a3b8]">${token.price_usd?.toFixed(2) || '1.00'}</span>
                    </div>
                    <span className="text-[#ccff00] font-bold mono">= ${token.balance_usd.toFixed(2)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between py-3 border-t-2 border-[#ccff00] mt-3">
                  <span className="text-white font-bold text-lg">Total Balance</span>
                  <span className="text-[#ccff00] font-bold text-lg mono">${portfolio.total_usd.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Wallet Addresses Section */}
          <div className="bg-[#0f172a] border border-[#1e293b] p-6">
            <h3 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
              Deposit Addresses
            </h3>
            {initialLoading ? (
              <div className="grid md:grid-cols-2 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="bg-[#020617] border border-[#1e293b] p-4 animate-pulse h-48" />
                ))}
              </div>
            ) : addresses.length === 0 ? (
              <p className="text-[#94a3b8] text-center py-8">No addresses yet</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {addresses.map((addr) => (
                  <motion.div
                    key={addr.address}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-[#020617] border border-[#1e293b] p-4 hover:border-[#ccff00]/30 transition-colors"
                    data-testid={`address-card-${addr.chain}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-lg font-bold text-white capitalize" style={{ fontFamily: 'Manrope' }}>
                        {addr.chain}
                      </h4>
                      <span className="text-xs text-[#94a3b8] uppercase tracking-wider">Deposit Address</span>
                    </div>
                    
                    <div className="bg-[#0f172a] border border-[#1e293b] p-3 mb-3">
                      <p className="mono text-xs text-[#94a3b8] break-all" data-testid={`address-text-${addr.chain}`}>
                        {addr.address}
                      </p>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => copyToClipboard(addr.address)}
                        className="flex-1 bg-[#1e293b] text-white text-sm px-4 py-2 hover:bg-[#334155] transition-colors flex items-center justify-center"
                        data-testid={`copy-address-${addr.chain}`}
                      >
                        <FaCopy className="mr-2" /> Copy
                      </button>
                      <button
                        onClick={() => setSelectedAddressForDeposit(addr)}
                        className="flex-1 bg-[#ccff00] text-black text-sm px-4 py-2 hover:bg-[#b3e600] transition-colors flex items-center justify-center font-bold"
                        data-testid={`deposit-button-${addr.chain}`}
                      >
                        Deposit
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Activity Section */}
        <div className="mt-12 bg-[#0f172a] border border-[#1e293b] p-6" data-testid="activity-section">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <FaHistory className="text-[#ccff00] text-2xl mr-3" />
                <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
                  Recent Activity
                </h3>
              </div>
              <button
                onClick={exportTransactions}
                className="flex items-center bg-transparent border border-[#94a3b8] text-[#94a3b8] hover:border-[#ccff00] hover:text-[#ccff00] text-sm px-4 py-2 transition-colors"
                data-testid="export-transactions-button"
              >
                <FaDownload className="mr-2" /> Export
              </button>
            </div>

            {initialLoading ? (
              <ActivitySkeleton />
            ) : activity.length === 0 ? (
              <p className="text-[#94a3b8] text-center py-8">No transactions yet. Simulate a deposit to get started!</p>
            ) : (
              <div className="space-y-3">
                {activity.map((entry, idx) => {
                  const tokenData = portfolio.tokens.find(t => t.symbol === entry.token);
                  const tokenColor = tokenData?.color || '#ccff00';
                  
                  return (
                    <motion.div
                      key={entry.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="bg-[#020617] border border-[#1e293b] p-4 flex items-center justify-between hover:border-[#ccff00]/30 transition-colors"
                      data-testid="activity-entry"
                    >
                      <div className="flex-1">
                        <div className="flex items-center mb-1">
                          <span className="text-white font-medium capitalize">{entry.type}</span>
                          <span className="text-[#94a3b8] mx-2">•</span>
                          <span className="text-[#94a3b8] text-sm capitalize">{entry.chain}</span>
                        </div>
                        <p className="mono text-xs text-[#94a3b8]">{entry.txhash}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold mono" style={{ color: tokenColor }}>
                          +{entry.amount} {entry.token}
                        </p>
                        <p className="text-xs text-[#94a3b8]">{new Date(entry.created_at).toLocaleDateString()}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
        </div>
      </div>

      {/* Token Detail Drawer (slide-up panel) */}
      {selectedToken && (
        <TokenDetailDrawer
          token={selectedToken}
          addresses={addresses}
          onClose={() => setSelectedToken(null)}
          onDeposit={(address) => {
            setSelectedToken(null);
            setSelectedAddressForDeposit(address);
          }}
        />
      )}

      {/* Mock Deposit Modal */}
      <AnimatePresence>
        {selectedAddressForDeposit && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 px-4"
            data-testid="mock-deposit-modal"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-[#0f172a] border border-[#ccff00] p-8 max-w-md w-full"
            >
              <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
                Simulate Deposit
              </h3>
              <p className="text-[#94a3b8] text-sm mb-6 capitalize">
                {selectedAddressForDeposit.chain} • {selectedAddressForDeposit.address.slice(0, 12)}...
              </p>

              <div className="mb-4">
                <label className="block text-xs uppercase tracking-widest text-[#94a3b8] mb-2">Select Token</label>
                <select
                  value={depositToken}
                  onChange={(e) => setDepositToken(e.target.value)}
                  className="w-full bg-[#020617] border border-[#1e293b] text-white px-4 h-12 focus:border-[#ccff00] focus:ring-1 focus:ring-[#ccff00] outline-none transition-colors"
                  data-testid="token-select"
                >
                  {allTokens.map((token) => (
                    <option key={token.symbol} value={token.symbol}>
                      {token.symbol} - {token.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-xs uppercase tracking-widest text-[#94a3b8] mb-2">
                  Amount ({depositToken})
                </label>
                <input
                  type="number"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  className="w-full bg-[#020617] border border-[#1e293b] text-white px-4 h-12 focus:border-[#ccff00] focus:ring-1 focus:ring-[#ccff00] outline-none transition-colors mono"
                  data-testid="deposit-amount-input"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setSelectedAddressForDeposit(null)}
                  className="flex-1 bg-transparent border border-[#94a3b8] text-[#94a3b8] px-4 py-3 hover:bg-[#1e293b] transition-colors"
                  data-testid="cancel-deposit-button"
                >
                  Cancel
                </button>
                <button
                  onClick={handleMockDeposit}
                  disabled={loading}
                  className="flex-1 bg-[#ccff00] text-black font-bold px-4 py-3 hover:bg-[#b3e600] transition-colors disabled:opacity-50"
                  style={{ fontFamily: 'Manrope' }}
                  data-testid="confirm-deposit-button"
                >
                  {loading ? 'Processing...' : 'Confirm Deposit'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Success Animation */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            className="fixed top-24 right-8 z-50"
          >
            <div className="bg-[#22c55e] border border-[#16a34a] p-6 flex items-center gap-4 shadow-lg">
              <FaCheckCircle className="text-white text-4xl" />
              <div>
                <p className="text-white font-bold text-lg">Deposit Successful!</p>
                <p className="text-white/80 text-sm">Balance updated</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}